import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { playChime } from "../lib/chime";
import { ArrowLeft, Download, Check, Hand, Video, BookOpen, Sparkles } from "lucide-react";

export default function LessonViewer() {
  const { courseId, lessonId } = useParams();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/lessons/${lessonId}`).then((r) => setLesson(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
    api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => {});
  }, [courseId, lessonId]);

  const completed = new Set(user?.progress?.completed_lessons || []);
  const isDone = completed.has(lessonId);

  const markComplete = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/progress/complete-lesson", { lesson_id: lessonId });
      setUser(data);
      playChime();
      toast.success("This teaching is complete. Let it settle within you.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const nextLesson = () => {
    if (!course) return null;
    const idx = course.lessons.findIndex((l) => l.id === lessonId);
    return idx >= 0 && idx < course.lessons.length - 1 ? course.lessons[idx + 1] : null;
  };

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-24 grid place-items-center bg-sanctuary-deep">
        <div className="text-center max-w-md px-6">
          <Sparkles className="w-12 h-12 text-gold mx-auto mb-6" />
          <h1 className="font-display text-3xl text-parchment mb-3">A gate stands here</h1>
          <p className="text-text-muted mb-8">{error}</p>
          <Link to="/membership" className="px-7 py-3 rounded-full bg-gold text-sanctuary-deep font-medium hover:bg-gold-hover transition-colors" data-testid="lesson-membership-cta">Become a member</Link>
        </div>
      </div>
    );
  }

  if (!lesson) return <div className="min-h-screen pt-32 text-center text-text-muted">Preparing the teaching…</div>;

  const next = nextLesson();

  return (
    <div className="min-h-screen pt-28 pb-24 bg-sanctuary-deep">
      <div className="max-w-4xl mx-auto px-6">
        <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-2 text-text-muted hover:text-gold text-sm mb-6 transition-colors" data-testid="lesson-back">
          <ArrowLeft size={14} /> Back to the degree
        </Link>

        <div className="eyebrow mb-3">{lesson.subtitle}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-parchment font-light mb-8" data-testid="lesson-title">{lesson.title}</h1>

        {/* Video transmission */}
        {lesson.video_url && (
          <div className="mb-10">
            <div className="flex items-center gap-2 eyebrow mb-3"><Video size={14} /> Guided Transmission</div>
            <div className="relative rounded-2xl overflow-hidden gold-border aspect-video bg-black">
              <iframe src={lesson.video_url} title={lesson.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="lesson-video" />
            </div>
          </div>
        )}

        {/* Text scripture */}
        <div className="mb-10">
          <div className="flex items-center gap-2 eyebrow mb-3"><BookOpen size={14} /> The Teaching</div>
          <div className="rounded-2xl bg-sanctuary-card gold-border p-8">
            <p className="text-parchment/90 leading-loose whitespace-pre-line text-[1.05rem]" data-testid="lesson-content">{lesson.content}</p>
          </div>
        </div>

        {/* Hand positions */}
        {lesson.hand_positions?.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-2 eyebrow mb-3"><Hand size={14} /> Sacred Hand Positions</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {lesson.hand_positions.map((h, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-sanctuary-card gold-border p-4">
                  <span className="w-8 h-8 rounded-full bg-gold/10 grid place-items-center text-gold text-sm shrink-0">{i + 1}</span>
                  <span className="text-parchment/90 text-sm">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PDF scroll */}
        {lesson.pdf_url && (
          <a href={lesson.pdf_url} target="_blank" rel="noopener noreferrer"
            className="mb-10 flex items-center gap-4 rounded-2xl bg-sanctuary-card gold-border p-5 hover:border-gold/40 transition-colors group" data-testid="download-pdf-button">
            <div className="w-11 h-11 rounded-full bg-gold/10 grid place-items-center text-gold group-hover:bg-gold/20 transition-colors"><Download size={18} /></div>
            <div className="flex-1">
              <div className="font-display text-lg text-parchment">Sacred Study Scroll</div>
              <div className="text-sm text-text-muted">Downloadable PDF to print and keep</div>
            </div>
            <span className="text-gold text-sm">Download</span>
          </a>
        )}

        {/* Complete */}
        <div className="flex flex-wrap items-center gap-4 pt-6 border-t border-gold/12">
          <button onClick={markComplete} disabled={saving || isDone}
            className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-colors ${isDone ? "bg-sage/20 text-sage-light" : "bg-gold text-sanctuary-deep hover:bg-gold-hover"}`}
            data-testid="complete-lesson-button">
            <Check size={18} /> {isDone ? "Teaching complete" : saving ? "Honouring…" : "Mark as complete"}
          </button>
          {next && (
            <button onClick={() => navigate(`/courses/${courseId}/lessons/${next.id}`)} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full gold-border text-parchment hover:bg-gold/10 transition-colors" data-testid="next-lesson-button">
              Next teaching →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
